import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'

export type PauseDuration = 
  | { type: 'days'; value: number }
  | { type: 'until'; value: string } // ISO date string
  | { type: 'forever' }

interface PauseDurationModalProps {
  isOpen: boolean
  challengeTitle: string
  onConfirm: (duration: PauseDuration) => void
  onCancel: () => void
}

export function PauseDurationModal({
  isOpen,
  challengeTitle,
  onConfirm,
  onCancel
}: PauseDurationModalProps) {
  const { t } = useTranslation()
  const [selectedOption, setSelectedOption] = useState<string>('1day')
  const [customDate, setCustomDate] = useState('')

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  const handleConfirm = () => {
    let duration: PauseDuration

    switch (selectedOption) {
      case '1day':
        duration = { type: 'days', value: 1 }
        break
      case '7days':
        duration = { type: 'days', value: 7 }
        break
      case '1month':
        duration = { type: 'days', value: 30 }
        break
      case 'custom':
        if (!customDate) return
        duration = { type: 'until', value: new Date(customDate + 'T23:59:59').toISOString() }
        break
      case 'forever':
        duration = { type: 'forever' }
        break
      default:
        return
    }

    onConfirm(duration)
  }

  const getMinDate = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center p-lg"
      style={{ 
        zIndex: 2147483647, // Maximum z-index value
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
      onClick={onCancel}
    >
      {/* Modal content */}
      <div 
        className="relative bg-surface rounded-lg shadow-xl max-w-md w-full mx-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ 
          zIndex: 2147483647,
          position: 'relative'
        }}
      >
        <div className="p-xl">
          <h2 className="text-xl font-semibold text-text mb-sm">
            {t('challenges.pauseChallenge')}
          </h2>
          <p className="text-text-muted mb-lg">
            {t('challenges.howLongToPause')} "{challengeTitle}"?
          </p>

          <div className="space-y-md mb-xl">
            {/* Predefined options */}
            <label className="flex items-center space-x-sm cursor-pointer">
              <input
                type="radio"
                name="pauseDuration"
                value="1day"
                checked={selectedOption === '1day'}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="text-primary focus:ring-primary"
              />
              <span className="text-text">{t('challenges.oneDay')}</span>
            </label>

            <label className="flex items-center space-x-sm cursor-pointer">
              <input
                type="radio"
                name="pauseDuration"
                value="7days"
                checked={selectedOption === '7days'}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="text-primary focus:ring-primary"
              />
              <span className="text-text">{t('challenges.sevenDays')}</span>
            </label>

            <label className="flex items-center space-x-sm cursor-pointer">
              <input
                type="radio"
                name="pauseDuration"
                value="1month"
                checked={selectedOption === '1month'}
                onChange={(e) => setSelectedOption(e.target.value)}
                className="text-primary focus:ring-primary"
              />
              <span className="text-text">{t('challenges.oneMonth')}</span>
            </label>

            <div className="space-y-sm">
              <label className="flex items-center space-x-sm cursor-pointer">
                <input
                  type="radio"
                  name="pauseDuration"
                  value="custom"
                  checked={selectedOption === 'custom'}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-text">{t('challenges.untilSpecificDate')}</span>
              </label>

              {selectedOption === 'custom' && (
                <div className="ml-6">
                  <input
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    min={getMinDate()}
                    className="w-full px-md py-sm border border-border rounded-full bg-bg text-text focus:outline-none focus:ring-2 focus:ring-focus"
                    required
                  />
                </div>
              )}
            </div>

            <div className="border-t border-border pt-md mt-lg">
              <label className="flex items-center space-x-sm cursor-pointer">
                <input
                  type="radio"
                  name="pauseDuration"
                  value="forever"
                  checked={selectedOption === 'forever'}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-text">{t('challenges.forever')}</span>
                <span className="text-xs text-error">{t('challenges.thisWillDelete')}</span>
              </label>
            </div>
          </div>

          <div className="flex gap-md justify-end">
            <Button
              variant="secondary"
              onClick={onCancel}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedOption === 'custom' && !customDate}
              variant={selectedOption === 'forever' ? 'danger' : 'primary'}
            >
              {selectedOption === 'forever' ? t('challenges.deleteChallenge') : t('challenges.pauseChallenge')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
} 